import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { conversationRepository } from "../repositories";
import { conversationService } from "../services/conversation.service";
import { conversationSseService } from "../services/conversation-sse.service";
import { SSE_CONVERSATION_EVENT_TYPES } from "../models/sse-event.schema";
import { useNotificationStore } from "@/domains/notification/stores";
import { notificationSoundService } from "@/domains/notification/services/notification-sound.service";
const PENDING_MESSAGE_SOUND_INCREASE_TTL_MS = 10_000;
export const useConversationStore = defineStore("conversation", () => {
    // ===========================================================================
    // STATE
    // ===========================================================================
    const conversations = ref([]);
    const activeConversation = ref(null);
    const messages = ref([]);
    const filters = ref({});
    const isConnectingToSSE = ref(false);
    const hasNewMessages = ref(false);
    const unreadMessageNotificationCount = ref(0);
    const hasHydratedUnreadMessageNotificationCount = ref(false);
    const pendingUnreadMessageSoundIncrease = ref(null);
    const lastChatInteractionAt = ref(0);
    const isLoadingConversations = ref(false);
    const isLoadingMessages = ref(false);
    const isSubmitting = ref(false);
    const error = ref(null);
    // ===========================================================================
    // GETTERS
    // ===========================================================================
    const filteredConversations = computed(() => conversationService.filterConversations(conversations.value, filters.value));
    const sortedConversations = computed(() => conversationService.sortByLastMessage(filteredConversations.value));
    const isActiveConversationClosed = computed(() => activeConversation.value
        ? !conversationService.isActive(activeConversation.value)
        : false);
    const hasUnreadMessageNotifications = computed(() => unreadMessageNotificationCount.value > 0 || hasNewMessages.value);
    // ===========================================================================
    // ACTIONS
    // ===========================================================================
    async function fetchConversations() {
        isLoadingConversations.value = true;
        error.value = null;
        try {
            conversations.value = await conversationRepository.getConversations();
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Failed to load conversations";
        }
        finally {
            isLoadingConversations.value = false;
        }
    }
    async function fetchConversation(id) {
        try {
            const updated = await conversationRepository.getConversation(id);
            const idx = conversations.value.findIndex((c) => c.id === id);
            if (idx !== -1) {
                conversations.value[idx] = updated;
            }
            if (activeConversation.value?.id === id) {
                activeConversation.value = updated;
            }
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Failed to refresh conversation";
        }
    }
    async function fetchMessages(conversationId) {
        isLoadingMessages.value = true;
        try {
            messages.value = await conversationRepository.getMessages(conversationId);
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Failed to load messages";
        }
        finally {
            isLoadingMessages.value = false;
        }
    }
    async function createConversation(payload) {
        isSubmitting.value = true;
        error.value = null;
        try {
            const conversation = await conversationRepository.createConversation(payload);
            const exists = conversations.value.some((c) => c.id === conversation.id);
            if (!exists) {
                conversations.value = [conversation, ...conversations.value];
            }
            return conversation;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Failed to create conversation";
            throw err;
        }
        finally {
            isSubmitting.value = false;
        }
    }
    async function sendMessage(conversationId, payload) {
        isSubmitting.value = true;
        try {
            await conversationRepository.sendMessage(conversationId, payload);
            await fetchMessages(conversationId);
            await fetchConversations();
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Failed to send message";
            throw err;
        }
        finally {
            isSubmitting.value = false;
        }
    }
    async function closeConversation(conversationId) {
        isSubmitting.value = true;
        error.value = null;
        try {
            const updated = await conversationRepository.closeConversation(conversationId);
            const idx = conversations.value.findIndex((c) => c.id === updated.id);
            if (idx !== -1)
                conversations.value[idx] = updated;
            if (activeConversation.value?.id === updated.id) {
                activeConversation.value = updated;
            }
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Failed to close conversation";
            throw err;
        }
        finally {
            isSubmitting.value = false;
        }
    }
    async function editMessage(messageId, text) {
        try {
            const updated = await conversationRepository.editMessage(messageId, text);
            const idx = messages.value.findIndex((m) => m.id === messageId);
            if (idx !== -1) {
                messages.value[idx] = updated;
            }
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Failed to edit message";
            throw err;
        }
    }
    async function deleteMessage(messageId) {
        try {
            await conversationRepository.deleteMessage(messageId);
            const idx = messages.value.findIndex((m) => m.id === messageId);
            if (idx !== -1) {
                messages.value[idx] = {
                    ...messages.value[idx],
                    deleted_at: new Date().toISOString(),
                    message: "",
                };
            }
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Failed to delete message";
            throw err;
        }
    }
    async function fetchUnreadMessageNotificationCount(options = {}) {
        const previousUnreadCount = unreadMessageNotificationCount.value;
        const wasHydrated = hasHydratedUnreadMessageNotificationCount.value;
        try {
            const nextUnreadCount = (await conversationRepository.getUnreadMessageNotificationCount()).count;
            unreadMessageNotificationCount.value = nextUnreadCount;
            handleUnreadMessageSoundState({
                previousUnreadCount,
                nextUnreadCount,
                wasHydrated,
                playSound: options.playSound === true,
                changedConversationId: options.changedConversationId ?? null,
            });
            hasHydratedUnreadMessageNotificationCount.value = true;
        }
        catch {
            unreadMessageNotificationCount.value = 0;
        }
    }
    function handleUnreadMessageSoundState({ previousUnreadCount, nextUnreadCount, wasHydrated, playSound, changedConversationId, }) {
        if (!wasHydrated)
            return;
        const now = Date.now();
        const increased = nextUnreadCount > previousUnreadCount;
        if (increased && !playSound) {
            pendingUnreadMessageSoundIncrease.value = {
                previousUnreadCount,
                nextUnreadCount,
                observedAt: now,
            };
            return;
        }
        if (increased && playSound) {
            playMessageSound(previousUnreadCount, nextUnreadCount, changedConversationId);
            pendingUnreadMessageSoundIncrease.value = null;
            return;
        }
        if (!playSound) {
            if (pendingUnreadMessageSoundIncrease.value &&
                now - pendingUnreadMessageSoundIncrease.value.observedAt >
                    PENDING_MESSAGE_SOUND_INCREASE_TTL_MS) {
                pendingUnreadMessageSoundIncrease.value = null;
            }
            return;
        }
        const pendingIncrease = pendingUnreadMessageSoundIncrease.value;
        if (!pendingIncrease)
            return;
        if (now - pendingIncrease.observedAt > PENDING_MESSAGE_SOUND_INCREASE_TTL_MS) {
            pendingUnreadMessageSoundIncrease.value = null;
            return;
        }
        if (nextUnreadCount < pendingIncrease.nextUnreadCount) {
            pendingUnreadMessageSoundIncrease.value = null;
            return;
        }
        playMessageSound(pendingIncrease.previousUnreadCount, Math.max(nextUnreadCount, pendingIncrease.nextUnreadCount), changedConversationId);
        pendingUnreadMessageSoundIncrease.value = null;
    }
    function playMessageSound(previousUnreadCount, nextUnreadCount, changedConversationId) {
        notificationSoundService.playMessageIfAllowed({
            previousUnreadCount,
            nextUnreadCount,
            activeConversationId: activeConversation.value?.id ?? null,
            changedConversationId,
            lastChatInteractionAt: lastChatInteractionAt.value,
        });
    }
    async function markMessageNotificationsRead(conversationId) {
        try {
            const result = await conversationRepository.markMessageNotificationsRead(conversationId);
            if (result.updated > 0) {
                unreadMessageNotificationCount.value = Math.max(0, unreadMessageNotificationCount.value - result.updated);
            }
            clearConversationUnreadState(conversationId);
            hasNewMessages.value = false;
        }
        catch {
            // Read-state updates are best effort and should never interrupt chat use.
        }
    }
    function clearConversationUnreadState(conversationId) {
        const applyReadState = (conversation) => conversation.id === conversationId
            ? {
                ...conversation,
                unread_message_count: 0,
                has_unread_messages: false,
            }
            : conversation;
        conversations.value = conversations.value.map(applyReadState);
        if (activeConversation.value?.id === conversationId) {
            activeConversation.value = applyReadState(activeConversation.value);
        }
    }
    function noteChatReaderInteraction() {
        lastChatInteractionAt.value = Date.now();
    }
    function setActiveConversation(conversation) {
        activeConversation.value = conversation;
        if (conversation) {
            hasNewMessages.value = false;
        }
    }
    function setFilters(newFilters) {
        filters.value = newFilters;
    }
    function connectSSE() {
        isConnectingToSSE.value = true;
        conversationSseService.connect({
            onConnect() {
                isConnectingToSSE.value = false;
            },
            onEvent(event) {
                const { type, conversationId } = event;
                if (type === SSE_CONVERSATION_EVENT_TYPES.NOTIFICATIONS_CHANGED) {
                    useNotificationStore().handleRealtimeInvalidation();
                    fetchUnreadMessageNotificationCount();
                    fetchConversations();
                    return;
                }
                if (type === SSE_CONVERSATION_EVENT_TYPES.MESSAGE_CREATED ||
                    type === SSE_CONVERSATION_EVENT_TYPES.MESSAGE_UPDATED ||
                    type === SSE_CONVERSATION_EVENT_TYPES.MESSAGE_DELETED) {
                    if (conversationId &&
                        activeConversation.value?.id === conversationId) {
                        fetchMessages(conversationId);
                    }
                    fetchConversations();
                    if (type === SSE_CONVERSATION_EVENT_TYPES.MESSAGE_CREATED) {
                        fetchUnreadMessageNotificationCount({
                            playSound: true,
                            changedConversationId: conversationId ?? null,
                        });
                    }
                    if (conversationId !== activeConversation.value?.id) {
                        hasNewMessages.value = true;
                    }
                }
                if (type === SSE_CONVERSATION_EVENT_TYPES.CONVERSATION_CLOSED ||
                    type === SSE_CONVERSATION_EVENT_TYPES.CONVERSATION_INVALIDATED) {
                    if (conversationId)
                        fetchConversation(conversationId);
                }
                if (type === SSE_CONVERSATION_EVENT_TYPES.CONVERSATION_CREATED) {
                    fetchConversations();
                }
            },
            onDisconnect() {
                isConnectingToSSE.value = false;
            },
            onError() {
                isConnectingToSSE.value = false;
            },
        });
    }
    function disconnectSSE() {
        conversationSseService.disconnect();
        isConnectingToSSE.value = false;
    }
    function $reset() {
        conversations.value = [];
        activeConversation.value = null;
        messages.value = [];
        filters.value = {};
        isConnectingToSSE.value = false;
        hasNewMessages.value = false;
        unreadMessageNotificationCount.value = 0;
        hasHydratedUnreadMessageNotificationCount.value = false;
        pendingUnreadMessageSoundIncrease.value = null;
        lastChatInteractionAt.value = 0;
        isLoadingConversations.value = false;
        isLoadingMessages.value = false;
        isSubmitting.value = false;
        error.value = null;
        disconnectSSE();
    }
    return {
        // state
        conversations,
        activeConversation,
        messages,
        filters,
        isConnectingToSSE,
        hasNewMessages,
        unreadMessageNotificationCount,
        isLoadingConversations,
        isLoadingMessages,
        isSubmitting,
        error,
        // getters
        filteredConversations,
        sortedConversations,
        isActiveConversationClosed,
        hasUnreadMessageNotifications,
        // actions
        fetchConversations,
        fetchConversation,
        fetchMessages,
        createConversation,
        sendMessage,
        closeConversation,
        editMessage,
        deleteMessage,
        fetchUnreadMessageNotificationCount,
        markMessageNotificationsRead,
        noteChatReaderInteraction,
        setActiveConversation,
        setFilters,
        connectSSE,
        disconnectSSE,
        $reset,
    };
});
